import { EnvironmentOutlined } from '@ant-design/icons';
import { Skeleton, Table, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScopeButton } from '../components/buttons';
import type { LoginDetails } from './types';

type UserLoginHistoryProps = {
  history?: LoginDetails[];
  onClose: () => void;
  profileMode?: boolean;
  loading?: boolean;
  /** Optional timestamp formatter. Defaults to DD-MM-YYYY HH:mm:ss. */
  formatTimestamp?: (value?: Date | string | number) => string;
};

const formatLoginTimestamp = (value?: Date | string | number) => {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const UserLoginHistory = ({
  history,
  onClose,
  profileMode,
  loading = false,
  formatTimestamp = formatLoginTimestamp,
}: UserLoginHistoryProps) => {
  const { t } = useTranslation();

  const tableDataSource = useMemo(() => {
    if (!history) return [];
    return history.map((record, index) => ({
      ...record,
      key: String(record.loginTimestamp ?? index),
    }));
  }, [history]);

  const columns = [
    {
      title: t('users.login-date'),
      dataIndex: 'loginTimestamp',
      key: 'loginTimestamp',
      render: (_: unknown, record: LoginDetails) =>
        formatTimestamp(record.loginTimestamp),
    },
    {
      title: t('users.ip-address'),
      dataIndex: 'loginIP',
      key: 'loginIP',
    },
    {
      title: '',
      key: 'action',
      render: (_: unknown, record: LoginDetails) => (
        <a
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('users.verify-location')}
          href={`https://whatismyipaddress.com/ip/${record.loginIP}`}
        >
          <EnvironmentOutlined aria-hidden />
        </a>
      ),
    },
  ];

  const intro = (
    <Typography
      style={{
        fontSize: profileMode ? '12px' : '16px',
        fontWeight: 400,
        lineHeight: '24px',
        textAlign: 'left',
        color: 'var(--color-text-muted, #5a6a6a)',
        marginBottom: '40px',
        whiteSpace: 'pre-line',
      }}
    >
      {t('users.login-history-desc')}
    </Typography>
  );

  if (loading) {
    return (
      <>
        {profileMode && (
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {t('users.login-history')}
          </div>
        )}
        {intro}
        <Skeleton active paragraph={{ rows: 8 }} />
      </>
    );
  }

  return (
    <>
      {profileMode && (
        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          {t('users.login-history')}
        </div>
      )}
      {intro}
      <Table
        rowKey="key"
        columns={columns}
        dataSource={tableDataSource}
        pagination={{ pageSize: profileMode ? 10 : 8, showSizeChanger: false }}
        bordered
        size="middle"
      />
      {!profileMode && (
        <div className="rows-drawer-footer" style={{ marginTop: '40px' }}>
          <ScopeButton
            type="default"
            width="100%"
            height="40px"
            bg_color="var(--color-layout-bg, #E9F6F7)"
            onClick={onClose}
          >
            {t('common.close')}
          </ScopeButton>
        </div>
      )}
    </>
  );
};
